import { forwardRef } from 'react'
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  type LucideIcon,
} from 'lucide-react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@/lib/utils/cn'
import { Tooltip } from '../Tooltip'

export interface SidebarProps {
  collapsed?: boolean
  /** Brand / logo block at the top of the rail. */
  header?: React.ReactNode
  /** User avatar + name + sign-out in the footer. */
  footer?: React.ReactNode
  children: React.ReactNode
  className?: string
}

/**
 * Primary navigation rail. 240px expanded / 48px collapsed. Collapse state
 * is the parent's concern — wire to the preferences store (M1) so it
 * persists per-user per-session.
 */
export function Sidebar({
  collapsed = false,
  header,
  footer,
  children,
  className,
}: SidebarProps) {
  return (
    <aside
      role="navigation"
      aria-label="Primary"
      data-collapsed={collapsed || undefined}
      className={cn(
        'flex h-full flex-col border-r border-border-subtle bg-surface-sunk transition-[width] duration-normal ease-standard',
        collapsed
          ? 'w-[var(--layout-sidebar-width-collapsed)]'
          : 'w-[var(--layout-sidebar-width)]',
        className,
      )}
    >
      {header ? (
        <header className="flex h-[var(--layout-topbar-height)] items-center gap-2 border-b border-border-subtle px-3">
          {header}
        </header>
      ) : null}
      <div className="flex-1 overflow-y-auto py-2">{children}</div>
      {footer ? (
        <footer className="border-t border-border-subtle p-2">{footer}</footer>
      ) : null}
    </aside>
  )
}

/**
 * Sidebar section header + items.
 *
 * `collapsed` (sidebar-wide icon-only mode) hides the label and renders
 * items as icons only. `sectionCollapsed` (Sprint 13a per-section
 * collapse) hides the items but keeps the label visible with a chevron
 * toggle. The two are independent: in icon-only mode the chevron is
 * irrelevant because there's no label.
 *
 * Sprint 13b — when `sortableIds` + `onReorder` are provided, the
 * section's items become draggable via dnd-kit. The grip-handle UI
 * is per-item (rendered by `SidebarItem`), but the SortableContext
 * + DndContext wrapper lives here so siblings can reorder without
 * the consumer wiring contexts manually.
 *
 * When `dragId` is provided, the SECTION ITSELF becomes draggable
 * among sibling sections — the parent must wrap its list of
 * `SidebarSection`s in `SidebarSectionList`. Independent of the
 * item-level `sortableIds`/`onReorder` above; a section can support
 * both at once.
 */
export interface SidebarSectionProps {
  label: string
  children: React.ReactNode
  collapsed?: boolean
  /** Sprint 13a — per-section collapse toggle (header chevron). */
  sectionCollapsed?: boolean
  /** Sprint 13a — invoked when the user clicks the section header. */
  onToggleSectionCollapsed?: () => void
  /** Sprint 13b — ordered child ids to make this section's items sortable. */
  sortableIds?: ReadonlyArray<string>
  /** Sprint 13b — fires on item drag-end with the new id order. */
  onReorder?: (next: ReadonlyArray<string>) => void
  /**
   * When set, this section participates in top-level category
   * drag-to-reorder — the id must match an entry in the parent
   * `SidebarSectionList`'s `ids`. Renders a grip handle on hover,
   * to the left of the label; the label/chevron stay click-only.
   */
  dragId?: string
}

export function SidebarSection(props: SidebarSectionProps) {
  if (props.dragId !== undefined && !props.collapsed) {
    return <SortableSidebarSection {...props} />
  }
  return <PlainSidebarSection {...props} />
}

function PlainSidebarSection({
  label,
  children,
  collapsed = false,
  sectionCollapsed = false,
  onToggleSectionCollapsed,
  sortableIds,
  onReorder,
}: SidebarSectionProps) {
  // In icon-only mode the section header is hidden, so there's nothing
  // to toggle; always render items.
  const itemsHidden = !collapsed && sectionCollapsed
  const Chevron = sectionCollapsed ? ChevronRight : ChevronDown
  const canToggle = !collapsed && Boolean(onToggleSectionCollapsed)
  const isSortable =
    !collapsed && Array.isArray(sortableIds) && Boolean(onReorder)

  return (
    <div role="group" className="mb-2">
      {!collapsed ? (
        canToggle ? (
          <button
            type="button"
            onClick={onToggleSectionCollapsed}
            aria-expanded={!sectionCollapsed}
            className="text-role-section-label flex w-full items-center justify-between border-none bg-transparent px-3 pb-1 pt-2 text-left text-tertiary transition-colors duration-fast hover:text-secondary"
          >
            <span>{label}</span>
            <Chevron size={12} aria-hidden="true" className="flex-shrink-0" />
          </button>
        ) : (
          <div className="text-role-section-label px-3 pb-1 pt-2">{label}</div>
        )
      ) : null}
      {!itemsHidden ? (
        isSortable ? (
          <SortableSidebarList
            ids={sortableIds!}
            onReorder={onReorder!}
          >
            {children}
          </SortableSidebarList>
        ) : (
          <ul role="list" className="flex flex-col gap-0.5">
            {children}
          </ul>
        )
      ) : null}
    </div>
  )
}

/**
 * Draggable variant of `SidebarSection` — the section's root div is a
 * dnd-kit sortable node, activated only via its own grip handle (same
 * "grip is the sole drag activator" contract as `SortableSidebarItem`)
 * so clicking the label/chevron still only toggles collapse. Requires
 * a `SidebarSectionList` ancestor providing the `SortableContext`.
 */
function SortableSidebarSection({
  label,
  children,
  sectionCollapsed = false,
  onToggleSectionCollapsed,
  sortableIds,
  onReorder,
  dragId,
}: SidebarSectionProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: dragId! })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  } as React.CSSProperties

  const itemsHidden = sectionCollapsed
  const Chevron = sectionCollapsed ? ChevronRight : ChevronDown
  const canToggle = Boolean(onToggleSectionCollapsed)
  const isSortableItems = Array.isArray(sortableIds) && Boolean(onReorder)

  return (
    <div ref={setNodeRef} style={style} role="group" className="group/section mb-2">
      <div className="flex items-center gap-0.5 py-1 pl-1 pr-3">
        <button
          type="button"
          aria-label={`Reorder ${label} section`}
          className="flex h-5 w-4 flex-shrink-0 cursor-grab items-center justify-center text-tertiary opacity-0 transition-opacity duration-fast hover:text-secondary group-hover/section:opacity-100 focus-visible:opacity-100 active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={12} aria-hidden="true" />
        </button>
        {canToggle ? (
          <button
            type="button"
            onClick={onToggleSectionCollapsed}
            aria-expanded={!sectionCollapsed}
            className="text-role-section-label flex flex-1 items-center justify-between border-none bg-transparent p-0 text-left text-tertiary transition-colors duration-fast hover:text-secondary"
          >
            <span>{label}</span>
            <Chevron size={12} aria-hidden="true" className="flex-shrink-0" />
          </button>
        ) : (
          <div className="text-role-section-label flex-1">{label}</div>
        )}
      </div>
      {!itemsHidden ? (
        isSortableItems ? (
          <SortableSidebarList ids={sortableIds!} onReorder={onReorder!}>
            {children}
          </SortableSidebarList>
        ) : (
          <ul role="list" className="flex flex-col gap-0.5">
            {children}
          </ul>
        )
      ) : null}
    </div>
  )
}

/**
 * Wraps a list of `SidebarSection`s (each given a `dragId`) in dnd-kit's
 * DndContext + a vertical SortableContext so top-level categories can be
 * dragged to reorder, mirroring `SortableSidebarList`'s per-item pattern
 * one level up. Consumers don't need to import dnd-kit themselves.
 */
export function SidebarSectionList({
  ids,
  onReorder,
  children,
}: {
  ids: ReadonlyArray<string>
  onReorder: (next: ReadonlyArray<string>) => void
  children: React.ReactNode
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    const next = [...ids]
    const [moved] = next.splice(oldIndex, 1)
    next.splice(newIndex, 0, moved)
    onReorder(next)
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext items={[...ids]} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  )
}

/**
 * Internal — wraps the section's items in dnd-kit's DndContext + a
 * vertical SortableContext so consumers don't have to import dnd-kit
 * at all. Each child should be a `<SidebarItem dragId={...}>` or it
 * won't participate in the sort.
 */
function SortableSidebarList({
  ids,
  onReorder,
  children,
}: {
  ids: ReadonlyArray<string>
  onReorder: (next: ReadonlyArray<string>) => void
  children: React.ReactNode
}) {
  // Activation distance of 6px keeps a click-without-drag from
  // hijacking item navigation. The Tooltip wrapper inside each
  // collapsed item also benefits from the threshold.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    const next = [...ids]
    const [moved] = next.splice(oldIndex, 1)
    next.splice(newIndex, 0, moved)
    onReorder(next)
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext items={[...ids]} strategy={verticalListSortingStrategy}>
        <ul role="list" className="flex flex-col gap-0.5">
          {children}
        </ul>
      </SortableContext>
    </DndContext>
  )
}

export interface SidebarItemProps {
  icon: LucideIcon
  label: string
  active?: boolean
  collapsed?: boolean
  /** Inline count badge — usually from `badgeCount` in the manifest. */
  badge?: React.ReactNode
  /** Tint of the badge — danger for blocker counts. */
  badgeTone?: 'neutral' | 'warning' | 'danger'
  /** Keyboard shortcut string rendered in the tooltip when collapsed. */
  shortcut?: string
  onClick?: () => void
  /** Optional href — rendered as <a> instead of <button>. */
  href?: string
  /**
   * Sprint 13b — when set, this item participates in dnd-kit
   * sortable inside its `SidebarSection`. The id must match an
   * entry in the section's `sortableIds`. Renders a grip handle on
   * hover; the rest of the row stays click-only navigation.
   */
  dragId?: string
}

export const SidebarItem = forwardRef<HTMLElement, SidebarItemProps>(function SidebarItem(
  props,
  ref,
) {
  // Sprint 13b — when dragId is set, route through the sortable
  // wrapper. The non-draggable code path stays as-is for callers
  // that opt out (the existing behavior every consumer relies on).
  if (props.dragId !== undefined && !props.collapsed) {
    return <SortableSidebarItem {...props} ref={ref} />
  }
  return <PlainSidebarItem {...props} ref={ref} />
})

const PlainSidebarItem = forwardRef<HTMLElement, SidebarItemProps>(function PlainSidebarItem(
  {
    icon: Icon,
    label,
    active = false,
    collapsed = false,
    badge,
    badgeTone = 'neutral',
    shortcut,
    onClick,
    href,
  },
  ref,
) {
  const inner = (
    <>
      <Icon size={16} className={cn('flex-shrink-0', active ? 'text-accent' : 'text-tertiary')} />
      {!collapsed ? (
        <>
          <span className={cn('min-w-0 flex-1 truncate text-sm', active ? 'font-medium' : 'font-normal')}>
            {label}
          </span>
          {badge !== undefined && badge !== null ? (
            <span
              className={cn(
                'text-role-badge-count inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1.5',
                badgeTone === 'neutral' && 'bg-muted text-secondary',
                badgeTone === 'warning' && 'bg-warning-bg text-warning-text',
                badgeTone === 'danger' && 'bg-danger-bg text-danger-text',
              )}
            >
              {badge}
            </span>
          ) : null}
        </>
      ) : null}
    </>
  )

  const itemClass = cn(
    'flex w-full cursor-pointer items-center gap-2 rounded-sm border-none border-l-2 border-l-transparent bg-transparent px-3 py-1.5 text-left text-primary no-underline transition-colors duration-fast',
    'hover:bg-surface-hover',
    active && 'border-l-accent bg-accent-subtle',
    collapsed && 'justify-center px-0',
  )

  const element = href ? (
    <a
      ref={ref as React.Ref<HTMLAnchorElement>}
      href={href}
      aria-current={active ? 'page' : undefined}
      className={itemClass}
      onClick={onClick}
    >
      {inner}
    </a>
  ) : (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      aria-current={active ? 'page' : undefined}
      className={itemClass}
      onClick={onClick}
    >
      {inner}
    </button>
  )

  const li = <li>{element}</li>

  if (collapsed) {
    return (
      <Tooltip label={label} shortcut={shortcut} side="right">
        {li}
      </Tooltip>
    )
  }
  return li
})

/**
 * Sprint 13b — draggable variant. Renders a hover-revealed grip
 * handle on the left of the row that is the only drag activator;
 * the rest of the row stays click-only navigation. Uses dnd-kit's
 * `useSortable` — the parent `SortableContext` (in `SidebarSection`)
 * is required.
 */
const SortableSidebarItem = forwardRef<HTMLElement, SidebarItemProps>(function SortableSidebarItem(
  {
    icon: Icon,
    label,
    active = false,
    badge,
    badgeTone = 'neutral',
    onClick,
    href,
    dragId,
  },
  _ref,
) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: dragId! })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  } as React.CSSProperties

  const inner = (
    <>
      <Icon size={16} className={cn('flex-shrink-0', active ? 'text-accent' : 'text-tertiary')} />
      <span className={cn('min-w-0 flex-1 truncate text-sm', active ? 'font-medium' : 'font-normal')}>
        {label}
      </span>
      {badge !== undefined && badge !== null ? (
        <span
          className={cn(
            'text-role-badge-count inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1.5',
            badgeTone === 'neutral' && 'bg-muted text-secondary',
            badgeTone === 'warning' && 'bg-warning-bg text-warning-text',
            badgeTone === 'danger' && 'bg-danger-bg text-danger-text',
          )}
        >
          {badge}
        </span>
      ) : null}
    </>
  )

  const itemClass = cn(
    'flex w-full cursor-pointer items-center gap-2 rounded-sm border-none border-l-2 border-l-transparent bg-transparent py-1.5 text-left text-primary no-underline transition-colors duration-fast',
    'group/item pl-1 pr-3',
    'hover:bg-surface-hover',
    active && 'border-l-accent bg-accent-subtle',
  )

  const navEl = href ? (
    <a
      href={href}
      aria-current={active ? 'page' : undefined}
      className="flex min-w-0 flex-1 items-center gap-2 text-primary no-underline"
      onClick={onClick}
    >
      {inner}
    </a>
  ) : (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-left text-primary"
      onClick={onClick}
    >
      {inner}
    </button>
  )

  return (
    <li
      ref={setNodeRef as React.Ref<HTMLLIElement>}
      style={style}
      className={itemClass}
    >
      {/* Grip handle — only this element accepts pointer-down for drag */}
      <button
        type="button"
        aria-label={`Reorder ${label}`}
        className="flex h-5 w-4 flex-shrink-0 cursor-grab items-center justify-center text-tertiary opacity-0 transition-opacity duration-fast hover:text-secondary group-hover/item:opacity-100 focus-visible:opacity-100 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={12} aria-hidden="true" />
      </button>
      {navEl}
    </li>
  )
})

/** Escape hatch — rendered directly inside a section body for custom content
 *  (e.g. search input). Rarely used. */
export function SidebarCustom({ children }: { children: React.ReactNode }) {
  return <li className="px-3 py-1">{children}</li>
}

export interface MobileSidebarDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  header?: React.ReactNode
  footer?: React.ReactNode
  children: React.ReactNode
}

/**
 * Off-canvas nav for viewports below the shell's `1024px` breakpoint
 * (see `useIsMobile`). Built on the same Radix Dialog primitive as
 * `Modal`/`Drawer` so focus trap, Escape-to-close, backdrop-click-to-close,
 * and body-scroll-lock all come for free instead of being reimplemented.
 * Always renders the sidebar expanded (never the desktop icon-only rail —
 * icon-only nav isn't a usable touch target pattern).
 */
export function MobileSidebarDrawer({
  open,
  onOpenChange,
  header,
  footer,
  children,
}: MobileSidebarDrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-overlay bg-bg-overlay animate-in fade-in-0 duration-fast" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-y-0 left-0 z-modal w-[var(--layout-sidebar-width)] max-w-[85vw] outline-none',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-left',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left duration-normal',
          )}
        >
          <VisuallyHidden asChild>
            <DialogPrimitive.Title>Primary navigation</DialogPrimitive.Title>
          </VisuallyHidden>
          <Sidebar collapsed={false} header={header} footer={footer} className="border-r-0">
            {children}
          </Sidebar>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
